"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MagneticButton } from "./Effects";
import { useInstallTarget, withUtm } from "../lib/install";
import { BrowserIcon } from "./BrowserIcon";

gsap.registerPlugin(ScrollTrigger);

const CHECK = (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none">
    <path d="M3 8.5l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CROSS = (
  <svg className="w-4 h-4 shrink-0 opacity-25" viewBox="0 0 16 16" fill="none">
    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const FREE_FEATURES = [
  { text: "8D Rotation on YouTube, Spotify Web & Apple Music", has: true },
  { text: "Auto DJ beat-aware playlist transitions", has: true },
  { text: "8 presets + sub-variants (Warm, Deep, Punchy...)", has: true },
  { text: "Bass boost, clarity, reverb sliders", has: true },
  { text: "Floating dockable panel + visualizer", has: true },
  { text: "30-sec shareable video samples", has: true },
  { text: "60-sec previews of every Pro mode", has: true },
  { text: "Unlimited Surround / 3D / Binaural", has: false },
  { text: "Full-length exports (up to 10 min)", has: false },
];

const PRO_FEATURES = [
  { text: "Everything in Free" },
  { text: "Multi-band Surround — lows planted, mids orbit, highs shimmer" },
  { text: "HRTF 3D — Orbit, Figure-8 & Drift motion paths" },
  { text: "Binaural Beats — Focus, Relax, Sleep & Meditate layers" },
  { text: "Full-length exports — up to 10 minutes per recording" },
  { text: "1080p MP4 video clips + lossless WAV + M4A" },
  { text: "Lifetime updates — every future Pro feature included" },
  { text: "No subscription. Pay once, keep forever" },
];

export default function Pricing() {
  const ref = useRef<HTMLDivElement>(null);
  const install = useInstallTarget();

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from("[data-pricing-card]", {
        scrollTrigger: { trigger: ref.current, start: "top 80%", toggleActions: "play none none none" },
        y: 40, autoAlpha: 0, duration: 0.6, stagger: 0.12, ease: "power2.out",
      });
      gsap.from("[data-pricing-card] li", {
        scrollTrigger: { trigger: ref.current, start: "top 70%", toggleActions: "play none none none" },
        x: -12, opacity: 0, duration: 0.35, stagger: 0.04, ease: "power2.out",
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section id="pricing" ref={ref} className="section">
      <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#FF3366] mb-3">
        Pricing
      </p>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
        Free forever.
        <br />
        <span className="text-[#b3b3b3] font-normal">Pro when you&apos;re ready.</span>
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
        {/* Free */}
        <div
          data-pricing-card
          className="relative p-8 rounded-lg"
          style={{ background: "var(--surface)", boxShadow: "var(--shadow-card)" }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b3b3b3]/75 mb-4">
            Free
          </p>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-4xl font-black text-white">$0</span>
            <span className="text-sm text-[#b3b3b3]">forever</span>
          </div>
          <p className="text-sm text-[#b3b3b3] mb-8">
            The full 8D experience on supported music players.
          </p>

          <ul className="space-y-3 mb-8">
            {FREE_FEATURES.map((f) => (
              <li key={f.text} className="flex items-start gap-3 text-sm">
                <span aria-hidden="true" className={`mt-0.5 ${f.has ? "text-[#2EC4B6]" : "text-white/50"}`}>
                  {f.has ? CHECK : CROSS}
                </span>
                <span className={f.has ? "text-[#b3b3b3]" : "text-[#b3b3b3]/70 line-through"}>
                  {f.text}
                </span>
              </li>
            ))}
          </ul>

          <a
            href={withUtm(install.baseUrl, "pricing")}
            target="_blank"
            rel="noopener noreferrer"
            title={`Install 8Dfy free for ${install.shortLabel}`}
            className="flex items-center justify-center gap-2 w-full text-center py-3 rounded-full text-[11px] font-bold uppercase tracking-[1.8px] text-[#b3b3b3] transition-all duration-200 hover:text-white hover:border-[#7c7c7c]"
            style={{ border: "1px solid #4d4d4d" }}
          >
            <BrowserIcon browser={install.browser} className="w-3.5 h-3.5" />
            Install Free for {install.shortLabel}
          </a>
        </div>

        {/* Pro — stacks first on mobile (the conversion target), original order on md+ */}
        <div
          data-pricing-card
          className="relative p-8 rounded-lg overflow-hidden order-first md:order-none"
          style={{ background: "var(--surface)", boxShadow: "var(--shadow-heavy)", border: "1px solid rgba(255,51,102,0.2)" }}
        >
          <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[80px] pointer-events-none" style={{ background: "rgba(255,51,102,0.08)" }} />

          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <p className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#FF3366]">
                Pro
              </p>
              <span className="text-[9px] font-bold uppercase tracking-[1px] px-2 py-0.5 rounded-full text-[#FF3366]" style={{ background: "rgba(255,51,102,0.12)", border: "1px solid rgba(255,51,102,0.2)" }}>
                Popular
              </span>
            </div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-black text-white">$4.99</span>
              <span className="text-sm text-[#b3b3b3]">one-time</span>
            </div>
            <p className="text-sm text-[#b3b3b3] mb-8">
              Unlock everything. Pay once, keep forever.
            </p>

            <ul className="space-y-3 mb-8">
              {PRO_FEATURES.map((f) => (
                <li key={f.text} className="flex items-start gap-3 text-sm">
                  <span className="text-[#FF3366] mt-0.5">{CHECK}</span>
                  <span className="text-[#b3b3b3]">{f.text}</span>
                </li>
              ))}
            </ul>

            <MagneticButton
              href="https://harshdev77.gumroad.com/l/inoxg?wanted=true"
              target="_blank"
              rel="noopener noreferrer"
              title="Buy the 8Dfy Pro license — $4.99 one-time, via Gumroad"
              className="group block w-full text-center py-3 rounded-full text-[11px] font-bold uppercase tracking-[1.8px] relative overflow-hidden transition-opacity duration-200 hover:opacity-90"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF3366] to-[#FF9F1C]" />
              <span className="relative text-white">Get Pro License &mdash; $4.99 <span aria-hidden="true">&#8599;</span></span>
            </MagneticButton>
            <p className="text-[10px] text-[#b3b3b3]/70 text-center mt-3 uppercase tracking-[1px]">
              Secure payment via Gumroad &middot; Instant license key by email &middot; Lifetime updates
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
